'use client';

import { SafeUser } from '@/app/types';
import { CheckCircle, Zap, TrendingUp } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';

interface HeroProps {
  currentUser?: SafeUser | null;
  subscription?: any;
}

const Hero: React.FC<HeroProps> = ({ currentUser, subscription }) => {
  const { data: session } = useSession();

  return (
    <div className="bg-white text-black">
      <Head>
        <title>SEOZast – 170+ Developers Choose Us Over $99/mo Tools</title>
        <meta
          name="description"
          content="Full SEO/pSEO + AI Search + Real-Time Analytics for $19.99/mo. Deploy to GitHub in 5 minutes. Trusted by 200+ developers and startups."
        />
      </Head>

      <section className="py-12 md:py-16 bg-white text-black">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">

          {/* MOBILE-FIRST LAYOUT */}
          <div className="text-center md:text-left md:grid md:grid-cols-2 md:gap-16 lg:gap-20 md:items-center">

            {/* TEXT CONTENT */}
            <div className="md:order-1">
              {/* SOCIAL PROOF BADGE */}
              {/* <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-3">
                <TrendingUp className="w-4 h-4" />
                <span>Trusted by 170+ Developers & Startups</span>
              </div> */}
              {/* LIVE BADGE – elegante */}
              <div className="inline-flex items-center gap-2 bg-black text-white px-4 py-1.5 rounded-full text-xs font-bold mb-6">
                <Zap className="w-3 h-3 animate-pulse" />
                500+ articles just went live
              </div>

              {/* MAIN HEADLINE */}
              <h1
                className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold mb-4"
                style={{ lineHeight: "1.2" }}
              >
                Rank on Google AND ChatGPT, for the price of a coffee
              </h1>

              {/* <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                Your SEO shouldn't cost more than your coffee
              </h1> */}

              {/* SUBHEADLINE */}
              <p className="text-base sm:text-lg text-neutral-700 mb-6 max-w-lg mx-auto md:mx-0 font-normal">
                Full SEO + AI Search + Real-Time Analytics + Backlinks + One-click publishing as low as <strong>$19.99/month</strong>
              </p>

              {/* MOBILE IMAGE */}
              <div className="md:hidden mb-8 flex justify-center">
                <Image
                  src="/images/seozast-article-home.png"
                  alt="SEOZast dashboard preview"
                  width={280}
                  height={200}
                  priority
                  className="w-[280px] h-auto max-w-full object-cover rounded-lg shadow-sm"
                  style={{ width: '280px', height: 'auto' }}
                />
              </div>

              {/* CTA BUTTON */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-md mx-auto md:mx-0">
                <Link
                  href={currentUser ? `/dashboard/${currentUser.id}` : '/register'}
                  className="bg-emerald-600 text-white hover:text-white px-8 py-3.5 rounded-lg font-semibold hover:bg-emerald-500 transition text-center shadow-sm"
                >
                  {session ? 'Go to Dashboard' : 'Automate your SEO in 5 Minutes'}
                </Link>
              </div>

              {/* SUB-CTA TEXT */}
              {/* <p className="text-sm text-neutral-600 mb-6 text-center md:text-left">
                No credit card • Export to any CMS • 63 languages
              </p> */}

              {/* 3 KEY BENEFITS */}
              <div className="space-y-4 mb-10 max-w-md mx-auto md:mx-0">
                {[
                  "20 long-tail keywords in 9 sec",
                  "Bing auto-index for +340 % AI impressions",
                  "Free Directory backlink — DA +12 fast"
                ].map((text, i) => (
                  <div key={i} className="flex items-center gap-3 text-left">
                    <Zap className="w-5 h-5 text-emerald-600" />
                    <span className="text-md font-medium text-gray-800">{text}</span>
                  </div>
                ))}
              </div>

              {/* BOTTOM FEATURES */}
              <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                  63 languages
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                  Works with any CMS
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                  No credit card
                </div>
              </div>
            </div>

            {/* DESKTOP IMAGE */}
            <div className="hidden md:flex md:order-2 w-full justify-center lg:justify-end">
              <Image
                src="/images/seozast-article-home.png"
                alt="SEOZast dashboard preview"
                width={450}
                height={320}
                priority
                className="w-[400px] lg:w-[450px] h-auto max-w-full object-cover "
                style={{ width: '450px', height: 'auto' }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pt-2 pb-3">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center mb-6 flex-wrap">
              <div className="flex items-center -space-x-4">
                {[
                  { src: '/images/avatars/serafima.jpg', alt: 'Serafima O' },
                  { src: '/images/avatars/eliana.jpg', alt: 'Eliana Jordan' },
                  { src: '/images/avatars/hadee.jpg', alt: 'Hadee Haque' },
                  { src: '/images/avatars/nikita.jpg', alt: 'Nikita Y' },
                  { src: '/images/avatars/vivien.jpg', alt: 'Vivien Mahe' },
                ].map((avatar, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    style={{ zIndex: 50 - index }}
                  >
                    <Image
                      src={avatar.src}
                      alt={avatar.alt}
                      width={40}
                      height={40}
                      className="w-10 h-10 rounded-full border-2 border-white shadow-lg 
                   transform transition-all duration-300 ease-in-out
                   group-hover:scale-110 group-hover:z-50 group-hover:shadow-2xl"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
              {/* CÓDIGO ORIGINAL COMENTADO - Con URLs de X/Twitter
              <div className="flex items-center -space-x-4">
                {[
                  'https://x.com/eliana_jordan/photo',
                  'https://x.com/hadeehaque/photo',
                  'https://x.com/NikitaY_Indie/photo',
                  'https://x.com/OSerafimaA/photo',
                  'https://x.com/VivienMahe/photo',
                ].map((src, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    style={{ zIndex: 50 - index }}
                  >
                    <img
                      src={src}
                      alt={`Indie Maker ${index + 1}`}
                      className="w-10 h-10 rounded-full border-2 border-white shadow-lg 
                   transform transition-all duration-300 ease-in-out
                   group-hover:scale-110 group-hover:z-50 group-hover:shadow-2xl"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
              */}
              <p className="text-sm font-semibold ml-4 max-w-[2400px] leading-tight text-left">
                Trusted by 200+ Founders
              </p>
            </div>
            <div className="flex justify-center items-center gap-4 my-6 pb-10">
              <a
                href="https://www.uneed.best/tool/seozast"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://www.uneed.best/POTD1.png"
                  alt="SEOZast Uneed Product of the Day"
                  className="h-8 sm:h-10 w-auto rounded"
                />
              </a>
              <a
                href="https://fazier.com/launches/seozast"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
              >
                <img
                  src="https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=4694&badge_type=weekly&theme=light"
                  alt="Fazier First Place Badge"
                  className="h-8 sm:h-10 w-auto"
                />
              </a>
              <a
                href="https://tinylaun.ch"
                target="_blank"
                rel="noopener"
                className="flex-shrink-0"
              >
                <img
                  src="https://tinylaun.ch/tinylaunch_badge_2.svg"
                  alt="TinyLaunch Badge"
                  className="h-8 sm:h-10 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Schema for SEO and GEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'What is SEOzast?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'SEOzast is an affordable SEO and GEO automation tool starting at $19.99/month. It helps you analyze keywords, generate AI articles optimized for search engines and AI platforms, and deploy to GitHub or download.'
              },
            },
            {
              '@type': 'Question',
              'name': 'Does SEOzast support GEO?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes, SEOzast now supports Generative Engine Optimization (GEO), creating AI-friendly content for platforms like ChatGPT, Perplexity, and Google SGE.'
              },
            },
            {
              '@type': 'Question',
              'name': 'How much does SEOzast cost?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'SEOzast starts at $19.99/month or $59.99/year for Basic, with Pro at $9.99/month or $99.99/year, and Enterprise at $29.99/month or $299.99/year.'
              },
            },
            {
              '@type': 'Question',
              'name': 'What is PeerQuik?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'PeerQuik is a SaaS boilerplate for rapid B2B and B2C launches, with built-in SEO and speed. It pairs perfectly with SEOzast for fast business growth.'
              },
            },
          ],
        })}
      </script>
    </div>
  );
};

export default Hero;

// 'use client';

// import { SafeUser } from '@/app/types';
// import { CheckCircle, Coffee } from 'lucide-react';
// import { useSession } from 'next-auth/react';
// import Head from 'next/head';
// import Image from 'next/image';
// import Link from 'next/link';

// interface HeroProps {
//     currentUser?: SafeUser | null;
//     subscription?: any;
// }

// const Hero: React.FC<HeroProps> = ({ currentUser, subscription }) => {
//     const { data: session } = useSession();

//     return (
//         <div className="bg-white text-black">
//             <Head>
//                 <title>SEOZast – Affordable SEO & AI Search Optimization for $5/Month</title>
//                 <meta
//                     name="description"
//                     content="Your SEO shouldn't cost more than your coffee. Analyze your site, find keywords, generate AI-optimized articles with backlinks, and deploy or download — all in one click."
//                 />
//             </Head>

//             <section className="py-12 md:py-16 bg-white text-black">
//                 <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">

//                     {/* MOBILE-FIRST LAYOUT */}
//                     <div className="text-center md:text-left md:grid md:grid-cols-2 md:gap-16 lg:gap-20 md:items-center">

//                         {/* TEXT CONTENT */}
//                         <div className="md:order-1">
//                             {/* <div className="inline-flex items-center bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full sm:text-sm text-xs font-medium mb-4">
//                                 <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
//                                 Now optimizing for AI search engines like ChatGPT
//                             </div> */}
//                             {/* REEMPLAZO DEL BADGE */}
//                             <div className="inline-flex items-center bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full sm:text-sm text-xs font-medium mb-4">
//                                 {/* <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span> */}
//                                 {/* <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold mr-1">
//                                     NEW
//                                 </span> Track your sites analytics in real-time */}
//                             </div>


//                             <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
//                                 Your SEO shouldn't cost more than your coffee
//                             </h1>

//                             <h3 className="text-base sm:text-lg text-neutral-700 mb-6 max-w-lg mx-auto md:mx-0 font-normal">
//                                 Generate AI-optimized articles, with automatic backlinks and <strong>real-time analytics</strong> for Google and AI search, deploy to GitHub, or download for <strong>$5.99/month</strong>.
//                             </h3>

//                             {/* MOBILE IMAGE */}
//                             <div className="md:hidden mb-8 flex justify-center">
//                                 {/* <img
//                                     src="/images/seozast-article-home.png"
//                                     alt="Preview of an article in SEOZast"
//                                     className="w-[280px] max-w-full"
//                                 /> */}
//                                 <Image
//                                     src="/images/seozast-article-home.png"
//                                     alt="Preview of an article in SEOZast"
//                                     width={280}
//                                     height={200}
//                                     sizes="(max-width: 768px) 280px"
//                                     className="w-[280px] max-w-full object-cover"
//                                     style={{ imageRendering: 'auto' }}
//                                 />
//                             </div>

//                             {/* BUTTONS - Más compactos en móvil */}
//                             <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-sm mx-auto md:mx-0">
//                                 <Link
//                                     href={currentUser ? `/dashboard/${currentUser.id}` : '/register'}
//                                     className="bg-emerald-600 text-white hover:text-white px-6 py-3 rounded-md font-medium hover:bg-emerald-500 transition text-center text-sm sm:text-base"
//                                 >
//                                     {session ? 'Go to Dashboard' : 'Try for Free'}
//                                 </Link>
//                                 <Link
//                                     href="/#pricing"
//                                     className="text-emerald-600 font-medium py-3 px-6 rounded-md border border-emerald-500 hover:bg-emerald-50 transition duration-300 shadow-sm text-center text-sm sm:text-base"
//                                 >
//                                     View pricing
//                                 </Link>
//                             </div>

//                             {/* FEATURES LIST - Vertical en móvil, horizontal en desktop */}
//                             <div className="space-y-2 md:space-y-0 md:flex md:flex-wrap md:gap-4 text-sm text-gray-600">
//                                 <div className="flex items-center justify-center md:justify-start">
//                                     <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
//                                     Works with CMS
//                                 </div>
//                                 <div className="flex items-center justify-center md:justify-start">
//                                     <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
//                                     63 languages
//                                 </div>
//                                 {/* <div className="flex items-center justify-center md:justify-start">
//                                     <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
//                                     Multiple tones
//                                 </div> */}
//                                 <div className="flex items-center justify-center md:justify-start">
//                                     <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
//                                     Cancel anytime
//                                 </div>
//                             </div>
//                         </div>

//                         {/* DESKTOP IMAGE - Solo visible en desktop */}
//                         <div className="hidden md:flex md:order-2 w-full justify-center lg:justify-end">
//                             <Image
//                                 src="/images/seozast-article-home.png"
//                                 alt="Preview of an article in SEOZast"
//                                 width={450}
//                                 height={320}
//                                 sizes="(min-width: 1024px) 450px, 400px"
//                                 className="w-[400px] lg:w-[450px] max-w-full object-cover"
//                                 style={{ imageRendering: 'auto' }}
//                             />
//                             {/* <img
//                                 src="/images/seozast-article-home.png"
//                                 alt="Preview of an article in SEOZast"
//                                 className="w-[400px] lg:w-[450px] max-w-full"
//                             /> */}
//                         </div>
//                     </div>
//                 </div>
//             </section>

//             {/* FAQ Schema for SEO and GEO */}
//             <script type="application/ld+json">
//                 {JSON.stringify({
//                     '@context': 'https://schema.org',
//                     '@type': 'FAQPage',
//                     'mainEntity': [
//                         {
//                             '@type': 'Question',
//                             'name': 'What is SEOzast?',
//                             'acceptedAnswer': {
//                                 '@type': 'Answer',
//                                 'text': 'SEOzast is an affordable SEO and GEO automation tool starting at $5.99/month. It helps you analyze keywords, generate AI articles optimized for search engines and AI platforms, and deploy to GitHub or download.'
//                             },
//                         },
//                         {
//                             '@type': 'Question',
//                             'name': 'Does SEOzast support GEO?',
//                             'acceptedAnswer': {
//                                 '@type': 'Answer',
//                                 'text': 'Yes, SEOzast now supports Generative Engine Optimization (GEO), creating AI-friendly content for platforms like ChatGPT, Perplexity, and Google SGE.'
//                             },
//                         },
//                         {
//                             '@type': 'Question',
//                             'name': 'How much does SEOzast cost?',
//                             'acceptedAnswer': {
//                                 '@type': 'Answer',
//                                 'text': 'SEOzast starts at $5.99/month or $59.99/year for Basic, with Pro at $9.99/month or $99.99/year, and Enterprise at $29.99/month or $299.99/year.'
//                             },
//                         },
//                         {
//                             '@type': 'Question',
//                             'name': 'What is PeerQuik?',
//                             'acceptedAnswer': {
//                                 '@type': 'Answer',
//                                 'text': 'PeerQuik is a SaaS boilerplate for rapid B2B and B2C launches, with built-in SEO and speed. It pairs perfectly with SEOzast for fast business growth.'
//                             },
//                         },
//                     ],
//                 })}
//             </script>
//         </div>
//     );
// };

// export default Hero;